import type {
  CSSProperties,
  FunctionComponent,
  HTMLAttributes,
  ReactElement,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Logger } from '../../../logger/Logger';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useIsLegacyDesktop } from '../../../styles/hooks/legacyMedia';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { BackButton } from '../../controls/backButton/BackButton';
import { IconButton, IconButtonSize } from '../../controls/iconButton/IconButton';
import { Icons } from '../../icons/iconNames';
import { useLegacyContext } from '../../legacyMode/LegacyContextProvider';

import styles from './Modal.module.css';

export type Alignment = 'start' | 'center' | 'end';

export type ModalSize = 'small' | 'regular' | number;

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  draggable?: boolean;
  footer?: ReactElement;
  fullScreenNoClose?: boolean;
  grow?: boolean;
  heightAdjustment?: number;
  isHistoryModal?: boolean;
  limitedHeight?: string;
  maxBodyHeight?: number;
  onBack?: () => void;
  onClose?: () => void;
  /** origin of placement on x axis relative to the container where this is rendered. Places it either before, after or inside of container.  */
  originX?: Alignment;
  /** alignment on y axis relative to the container bounding box where this is rendered.  */
  alignY?: Alignment;
  /** width of the modal window with two presets or numeric value to allow override width to anything
   * - small: 320px
   * - regular: 360px
   */
  size?: ModalSize;
  newLayout: boolean;
  title?: ReactElement | string;
  /** boundary from the bottom which modal cannot exceed expresses as offset from the bottom of the screen */
  bottomLimit?: number;
  tk?: string;
  'data-testid'?: string;
}

interface Vector {
  x: number;
  y: number;
}

export const Modal: FunctionComponent<ModalProps> = ({
  children,
  draggable,
  footer,
  fullScreenNoClose,
  grow,
  heightAdjustment,
  limitedHeight,
  maxBodyHeight,
  bottomLimit,
  onBack,
  onClose,
  open,
  originX = 'start',
  alignY = 'start',
  size,
  className,
  newLayout,
  title,
  tk,
  ...rest
}): ReactElement => {
  const { isLegacyMode } = useLegacyContext();
  const isLegacyDesktop = useIsLegacyDesktop();
  const [shouldRender, setShouldRender] = useState(open);
  const modalRef = useRef<HTMLDivElement>(null);
  const modalWindowRef = useRef<HTMLDivElement>(null);

  const [modalHeight, setModalHeight] = useState(0);
  const [modalWindowHeight, setModalWindowHeight] = useState(0);
  const hasReceivedSpawn = useRef(false);
  const hasDragHappened = useRef(false);

  const isDragged = useRef(false);

  const mods = useModifiers(
    styles,
    'modal',
    {
      draggable,
      grow,
      open,
      newLayout,
      limitedHeight: !!limitedHeight || !!bottomLimit,
      [`origin_${originX}`]: true,
      [`y_${alignY}`]: true,
    },
    { withBaseClass: true, extraClassName: className }
  );
  const [offset, setOffset] = useState<Vector>({ x: 0, y: 0 });

  useEffect(() => {
    let timeoutId: number;
    if (open) {
      setShouldRender(true);
      if (alignY === 'start') setOffset({ x: 0, y: 0 });
    } else {
      modalRef?.current?.classList.add(styles.modal_closing);
      timeoutId = window.setTimeout(() => {
        modalRef?.current?.classList.remove(styles.modal_closing);
        setShouldRender(false);
      }, 300);
      hasReceivedSpawn.current = false;
    }
    return (): void => window.clearTimeout(timeoutId);
  }, [open, alignY]);

  // track modal height changes
  useLayoutEffect(() => {
    const modalPtr = modalRef.current;
    const modalWidowPtr = modalWindowRef.current;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (entry.target === modalRef.current) {
          setModalHeight(height);
        } else if (entry.target === modalWindowRef.current) {
          setModalWindowHeight(height);
        }
      }
    });

    if (modalPtr && modalWidowPtr) {
      observer.observe(modalPtr);
      observer.observe(modalWidowPtr);
    }

    return (): void => {
      if (modalPtr && modalWidowPtr) {
        observer.unobserve(modalPtr);
        observer.unobserve(modalWidowPtr);
      }
    };
  }, [shouldRender]);

  // align "untouched" bottom modal to the bottom of the screen
  useLayoutEffect(() => {
    if (
      alignY === 'end' &&
      modalWindowHeight &&
      (!hasReceivedSpawn.current || !hasDragHappened.current) &&
      shouldRender
    ) {
      hasReceivedSpawn.current = true;
      setOffset({ x: 0, y: modalHeight - modalWindowHeight });
    }
  }, [modalHeight, alignY, modalWindowHeight, shouldRender]);

  const modalWidthAttribute = useMemo((): string | undefined => {
    switch (size) {
      case 'small':
        return 'var(--ui-size-modal-width-small)';
      case 'regular':
        return 'var(--ui-size-modal-width-regular)';
      default:
        return typeof size === 'number' ? size + 'px' : undefined;
    }
  }, [size]);

  const handlePointerDown = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (!draggable || !modalRef.current) return;

    let offsetVector: Vector = { ...offset };
    const clickOriginVector: Vector = {
      x: event.clientX,
      y: event.clientY,
    };

    const clampOffset = (offset: Vector): Vector => {
      if (offset.y < 0) {
        offset.y = 0;
      }
      return offset;
    };

    const updateOffsetVector = (event: MouseEvent): void => {
      offsetVector = clampOffset({
        x: event.clientX - clickOriginVector.x + offset.x,
        y: event.clientY - clickOriginVector.y + offset.y,
      });
      setOffset(offsetVector);
    };

    const handlePointerMove = (event: MouseEvent): void => {
      hasDragHappened.current = true;
      isDragged.current = true;
      updateOffsetVector(event);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    const handlePointerUp = (): void => {
      if (isDragged.current == true) {
        const dx = offset.x - offsetVector.x;
        const dy = offset.y - offsetVector.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        Logger.logEvent('modalDrag', {
          additional: rest['data-testid'],
          bandwidth: Math.floor(distance),
        });
        isDragged.current = false;
      }
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleClose = (): void => {
    onClose?.();
  };

  const handleBack = (): void => {
    if (onBack) {
      onBack();
    } else {
      handleClose();
    }
  };

  return (
    <div
      data-testid="modal"
      className={mods}
      ref={modalRef}
      style={
        {
          '--heightAdjustment': `${heightAdjustment ?? 0}px`,
          '--max-height': limitedHeight ?? '100%',
          '--bottom-limit': `${bottomLimit ?? 0}px`,
          '--modalWidth': modalWidthAttribute,
        } as CSSProperties
      }
    >
      {shouldRender ? (
        <div
          data-testid="modal-window"
          className={`${styles.modal__window} ${className}`}
          style={
            {
              transform: draggable ? `translate(${Math.round(offset.x)}px, ${Math.round(offset.y)}px)` : null,
            } as CSSProperties
          }
          ref={modalWindowRef}
          {...rest}
        >
          <div data-testid="modal-header" className={styles.modal__header} onPointerDown={handlePointerDown}>
            <div className={styles.modal__headerWing}>
              <BackButton onClick={handleBack} visible={!!onBack} />
            </div>
            <div data-testid="modal-header-title" className={styles.modal__headerTitle}>
              {tk ? <L10nText tk={tk} /> : title}
            </div>
            <div className={styles.modal__headerWing}>
              <div className={`${fullScreenNoClose ? styles.modal__action_fullScreenHidden : ''}`}>
                {onClose ? (
                  <IconButton
                    data-testid="modal-close-button"
                    ghost
                    icon={Icons.Close}
                    onClick={handleClose}
                    size={isLegacyMode && isLegacyDesktop ? IconButtonSize.SMALL : IconButtonSize.TINY}
                  />
                ) : null}
              </div>
            </div>
          </div>
          <div data-testid="modal-body" className={styles.modal__body} style={{ maxHeight: maxBodyHeight }}>
            {children}
          </div>
          {footer ? (
            <div data-testid="modal-footer" className={styles.modal__footer}>
              {footer}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
